namespace SaNumberUtils {
    // After toFixed(2), remove trailing fractional zeros, then a leftover decimal point.
    const trailingFractionZerosRegex = /0+$/;
    const trailingDecimalPointRegex = /\.$/;

    /** Formats USD amounts with two decimal places. */
    export function formatCurrency(value = 0): string {
        return value.toLocaleString("en-US", {
            style: "currency",
            currency: "USD",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
    }

    /** Formats SEUs to at most two decimal places, without trailing fractional zeros. */
    export function formatSeus(value = 0): string {
        return Number.isInteger(value)
            ? value.toFixed(0)
            : value
                  .toFixed(2)
                  .replace(trailingFractionZerosRegex, "")
                  .replace(trailingDecimalPointRegex, "");
    }

    /**
     * Coerces a value to a float. If the value is an array, it will attempt to coerce the first element. If coercion fails, NaN is returned.
     */
    export function coerceToFloat(
        value: string | string[] | number | null | undefined,
    ): number {
        if (typeof value === "number") {
            return value;
        }
        if (typeof value === "string") {
            return Number.parseFloat(sanitizeStringToNumeric(value));
        }
        if (Array.isArray(value) && value.length > 0) {
            return coerceToFloat(value[0]);
        }

        return Number.NaN;
    }

    /**
     * Coerces a value to a float or returns null if coercion fails. If the value is an array, it will attempt to coerce the first element.
     */
    export function coerceToNullableFloat(
        value: string | string[] | number | null | undefined,
    ): number | null {
        const coercedValue = coerceToFloat(value);
        return Number.isNaN(coercedValue) ? null : coercedValue;
    }

    /**
     * Coerces a value to an integer. If the value is an array, it will attempt to coerce the first element. If coercion fails, NaN is returned.
     */
    export function coerceToInteger(
        value: string | string[] | number | null | undefined,
    ): number {
        if (typeof value === "number") {
            return value;
        }
        if (typeof value === "string") {
            return Number.parseInt(sanitizeStringToNumeric(value), 10);
        }
        if (Array.isArray(value) && value.length > 0) {
            return coerceToInteger(value[0]);
        }

        return Number.NaN;
    }

    /**
     * Coerces a value to an integer or returns null if coercion fails. If the value is an array, it will attempt to coerce the first element.
     */
    export function coerceToNullableInteger(
        value: string | string[] | number | null | undefined,
    ): number | null {
        const coercedValue = coerceToInteger(value);
        return Number.isNaN(coercedValue) ? null : coercedValue;
    }

    function sanitizeStringToNumeric(text: string): string {
        return text.replaceAll(/[^0-9.-]/g, "");
    }

    /**
     * Compares two numeric values with a specified tolerance to determine if they are approximately equal.
     * This is useful for comparing floating-point numbers where exact equality is often unreliable due to precision issues.
     * @param value1 The first numeric value to compare.
     * @param value2 The second numeric value to compare.
     * @param tolerance The allowable difference between the two values for them to be considered approximately equal.
     */
    export function isProbablyEqual(
        value1: string | string[] | number | null | undefined,
        value2: string | string[] | number | null | undefined,
        tolerance: number = 0.01,
    ): boolean {
        const num1 = coerceToFloat(value1);
        const num2 = coerceToFloat(value2);

        if (Number.isNaN(num1) || Number.isNaN(num2)) {
            return false;
        }

        return Math.abs(num1 - num2) <= tolerance;
    }

    /**
     * Determines if the first numeric value is probably greater than the second, considering a specified tolerance.
     * @param value1 The first numeric value to compare.
     * @param value2 The second numeric value to compare.
     * @param tolerance The allowable difference for the comparison.
     */
    export function isProbablyGreaterThan(
        value1: string | string[] | number | null | undefined,
        value2: string | string[] | number | null | undefined,
        tolerance: number = 0.01,
    ): boolean {
        const num1 = coerceToFloat(value1);
        const num2 = coerceToFloat(value2);

        if (Number.isNaN(num1) || Number.isNaN(num2)) {
            return false;
        }

        return num1 > num2 + tolerance;
    }

    /**
     * Determines if the first numeric value is probably less than the second, considering a specified tolerance.
     * @param value1 The first numeric value to compare.
     * @param value2 The second numeric value to compare.
     * @param tolerance The allowable difference for the comparison.
     */
    export function isProbablyLessThan(
        value1: string | string[] | number | null | undefined,
        value2: string | string[] | number | null | undefined,
        tolerance: number = 0.01,
    ): boolean {
        const num1 = coerceToFloat(value1);
        const num2 = coerceToFloat(value2);

        if (Number.isNaN(num1) || Number.isNaN(num2)) {
            return false;
        }

        return num1 < num2 - tolerance;
    }
}

globalThis.SaNumberUtils = SaNumberUtils;
